import { recoveryCall } from "./recovery"
import {
    useMutation,
    useQuery,
    UseMutationOptions,
} from "@tanstack/react-query"
import {
    UserCredential,
    createUserWithEmailAndPassword,
    EmailAuthProvider,
    getAuth,
    reauthenticateWithCredential,
    signInWithEmailAndPassword,
    verifyPasswordResetCode,
} from "firebase/auth"
import { queryClient } from "./tsQueryConfig"
import { NavigateFunction } from "react-router-dom"
import { APP_ROUTES } from "@/common/urls"
import { auth, db, functions } from "@/common/lib/firebase/firebase"
import { DocumentData, doc, getDoc, setDoc } from "firebase/firestore"
import { httpsCallable } from "firebase/functions"
import toast from "react-hot-toast"
import { deleteUser } from "firebase/auth"
import {
    sendSecondaryEmailReminder,
    sendPasswordResetRequest,
} from "./mailService"

interface SignInDTO {
    email: string
    password: string
}

interface UpdatePasswordDTO {
    currentPassword: string
    newPassword: string
}

async function signInWithEmailPassword(dto: SignInDTO) {
    const auth = getAuth()
    const userCredentials = await signInWithEmailAndPassword(
        auth,
        dto.email,
        dto.password
    )
    return userCredentials
}

export const useSignInWithEmailAndPassword = (navFunc: NavigateFunction) => {
    return useMutation({
        mutationFn: (dto: SignInDTO) => signInWithEmailPassword(dto),
        onSuccess: async (data) => {
            localStorage.setItem("user", JSON.stringify(data))
            queryClient.invalidateQueries({
                queryKey: ["isAuthenticated"],
            })
            // Check for secondary email and send reminder if not present
            const user = data.user
            if (user.uid && user.email) {
                const userRef = doc(db, "users", user.uid)
                const userSnap = await getDoc(userRef)
                if (userSnap.exists()) {
                    const userData = userSnap.data()
                    if (
                        !userData.secondaryEmail &&
                        userData.role === "applicant"
                    ) {
                        await sendSecondaryEmailReminder([
                            {
                                email: user.email,
                                userName: userData.firstName,
                                orgName: userData.orgName || "",
                                loginUrl: `${window.location.origin}${APP_ROUTES.AUTH.LOGIN}`,
                            },
                        ]).catch(() => undefined)
                    }
                }
            }

            navFunc(APP_ROUTES.ROOT.DASHBOARD)
        },
        onError: (error) => {
            toast.error(`Failed to sign in. Try again. ${error.message}`)
        },
    })
}

export const useIsAuthenticated = () => {
    return useQuery<boolean>({
        queryKey: ["isAuthenticated"],
        queryFn: () => {
            const userDetails = localStorage.getItem("user")

            const isAuthenticated = !!userDetails

            return isAuthenticated
        },
        retry: false,
    })
}

async function signUserOut() {
    const auth = getAuth()
    return await auth.signOut()
}

export const useSignUserOut = (navFunction: NavigateFunction) => {
    return useMutation({
        mutationFn: signUserOut,
        onSuccess: () => {
            localStorage.removeItem("user")
            queryClient.clear()
            navFunction(APP_ROUTES.AUTH.LOGIN)
        },
    })
}

// Create new Account
type ApplicationDate = {
    seconds: number | null
    nanoseconds: number | null
}
interface AccountSetupDTO {
    password: string
    confirmPassword: string
    acceptTCs: boolean
    charityNumber: string
    country: string
    dateCreated: ApplicationDate
    email: string
    firstName: string
    lastName: string
    mobileNumber: string
    role: "trustee" | "applicant" | "admin"
    totalPastGrantAmount: null
    userId: string
}

type AccountPersistedFields = Omit<
    AccountSetupDTO,
    "password" | "confirmPassword" | "userId"
>

const excludePassword = (accountDto: AccountSetupDTO) => {
    const {
        password: _password,
        confirmPassword: _confirmPassword,
        userId: _userId,
        ...accountWithoutPassword
    }: AccountSetupDTO = accountDto
    return accountWithoutPassword as AccountPersistedFields
}

async function createUserWithEmailPassword(
    email: string,
    password: string
): Promise<UserCredential> {
    try {
        const userCredential = await createUserWithEmailAndPassword(
            auth,
            email,
            password
        )
        return userCredential
    } catch (error) {
        throw new Error("Failed to create user")
    }
}

async function createFirestoreDocumentAndRead(
    uid: string,
    accountData: AccountSetupDTO
): Promise<DocumentData> {
    try {
        // Exclude the password from the account data
        const accountWithoutPassword = excludePassword(accountData)
        const accountToSave = {
            ...accountWithoutPassword,
            userId: uid,
        }

        const userRef = doc(db, "users", uid)
        await setDoc(userRef, accountToSave)

        // Read the document data
        const docSnap = await getDoc(userRef)

        if (docSnap.exists()) {
            return docSnap.data() as DocumentData
        } else {
            throw new Error("No document found after creation")
        }
    } catch (error) {
        throw new Error("Failed to create or read Firestore document")
    }
}

async function createNewAccount(accountDto: AccountSetupDTO) {
    let createdUserCredential: UserCredential | null = null
    try {
        createdUserCredential = await createUserWithEmailPassword(
            accountDto.email,
            accountDto.password
        )
        const { user } = createdUserCredential

        await createFirestoreDocumentAndRead(user.uid, accountDto)
        return createdUserCredential
    } catch (error) {
        if (createdUserCredential?.user) {
            await deleteUser(createdUserCredential.user).catch(
                (cleanupError) => {
                    console.error(
                        "Failed to cleanup auth user after registration error:",
                        cleanupError
                    )
                }
            )
        }
        throw new Error("Failed to create new account")
    }
}

export const useCreateNewAccount = () => {
    return useMutation({
        mutationFn: (accountDto: AccountSetupDTO) =>
            createNewAccount(accountDto),
        onSuccess: (data) => {
            localStorage.setItem("user", JSON.stringify(data))
            queryClient.invalidateQueries({
                queryKey: ["isAuthenticated"],
            })
            queryClient.refetchQueries({
                queryKey: ["isAuthenticated"],
            })
            queryClient.invalidateQueries({
                queryKey: ["userDetails"],
            })
            queryClient.refetchQueries({
                queryKey: ["userDetails"],
            })
            toast.success("Account created successfully")
        },
        onError: (error) => {
            toast.error(`Failed to create account. Try again. ${error.message}`)
        },
    })
}

// Check if there is a user with the same OSCR number
export async function checkOSCRNumber(dto: string) {
    try {
        const checkAvailability = httpsCallable<
            { charityNumber: string },
            { inUse: boolean }
        >(functions, "checkCharityNumberAvailability")
        const result = await checkAvailability({ charityNumber: dto })
        return result.data.inUse
    } catch {
        throw new Error("Unable to validate charity number at this time.")
    }
}

interface ResetPasswordEmailDTO {
    email: string
}

async function requestPasswordResetEmail(
    dto: ResetPasswordEmailDTO
): Promise<void> {
    await sendPasswordResetRequest([{ email: dto.email }])
}

export const useRequestPasswordResetEmail = (navFunc?: NavigateFunction) => {
    return useMutation({
        mutationFn: (dto: ResetPasswordEmailDTO) =>
            requestPasswordResetEmail(dto),
        onSuccess: () => {
            toast.success("If this email exists, a reset link has been sent.")
            navFunc?.(APP_ROUTES.AUTH.LOGIN)
        },
        onError: (error) => {
            toast.error(`Reset failed. ${error.message}`)
        },
    })
}

export const useVerifyPasswordResetCode = (oobCode: string) => {
    return useQuery({
        queryKey: ["verifyPasswordResetCode", oobCode],
        queryFn: async () => {
            const auth = getAuth()
            return await verifyPasswordResetCode(auth, oobCode)
        },
        enabled: !!oobCode,
        retry: false,
    })
}

export const useCompletePasswordReset = (
    oobCode: string,
    options?: { onSuccess?: () => void }
) => {
    return useMutation({
        mutationFn: async (newPassword: string) => {
            await recoveryCall("completePasswordReset", {
                oobCode,
                newPassword,
            })
        },
        onSuccess: () => {
            toast.success("Password reset successful.")
            options?.onSuccess?.()
            // navFunc?.(APP_ROUTES.AUTH.LOGIN)
        },
        onError: (error) => {
            toast.error(`Reset failed. ${error.message}`)
        },
    })
}

async function updateUserPassword(dto: UpdatePasswordDTO) {
    const auth = getAuth()
    await auth.authStateReady()
    const user = auth.currentUser
    if (!user || !user.email) {
        throw new Error("User not authenticated")
    }

    const credential = EmailAuthProvider.credential(
        user.email,
        dto.currentPassword
    )
    await reauthenticateWithCredential(user, credential)
    await user.getIdToken(true)
    await recoveryCall("changeAccountPassword", {
        newPassword: dto.newPassword,
    })
    await auth.signOut()
    localStorage.removeItem("user")
    queryClient.clear()
}

export const useUpdateUserPassword = (
    options?: UseMutationOptions<void, unknown, UpdatePasswordDTO>
) => {
    return useMutation({
        mutationFn: (dto: UpdatePasswordDTO) => updateUserPassword(dto),
        ...options,
    })
}

export interface TransferOwnershipDTO {
    userId: string
    action: "accept" | "reject"
}

export interface HandleAccountOwnershipTransferResponse {
    success: boolean
    message: string
}

export const transferAccountOwnership = async (
    dto: TransferOwnershipDTO
): Promise<HandleAccountOwnershipTransferResponse> => {
    const auth = getAuth()
    await auth.authStateReady()
    const user = auth.currentUser
    if (!user) {
        throw new Error("User not authenticated")
    }
    const { userId, action } = dto
    try {
        const transferAccountOwnershipRequest = httpsCallable<
            { userId: string; action: "accept" | "reject" },
            HandleAccountOwnershipTransferResponse
        >(functions, "handleAccountOwnershipTransfer")
        const result = await transferAccountOwnershipRequest({ userId, action })
        return result.data
    } catch {
        throw new Error("Unable to transfer account ownership at this time.")
    }
}
